import { isGaffRetained } from "@/api/affdo";
import type { DihedralProfile } from "@/api/affdo";

/**
 * How a `DihedralProfile` is read before it is plotted — shared by the
 * per-run panel (`DihedralProfilePlot`) and the cross-run overlay
 * (`TorsionOverlayChart`).
 *
 * These four helpers moved out of `DihedralProfilePlot` when VIZ U2.1 added a
 * second CHART SHAPE over the same artifacts. The overlay is not a fork of the
 * panel — a panel draws one torsion's three curves, an overlay draws one curve
 * per (run, torsion) on shared axes, and no amount of props makes those the
 * same component. What must not diverge is the READING: which arrays are
 * trusted, and above all *when the absence of a fitted curve may be captioned
 * as a scientific decision*. That rule (#869/#878, and Rupert's block on #878)
 * now has exactly one implementation, so a change to it reaches both surfaces
 * or neither.
 *
 * The two vague-but-true fallbacks are exported as constants (#1370) because a
 * THIRD reader needs them: the shared key over a grid of small multiples falls
 * back to them when the panels beneath it do not all declare the same level or
 * the same base force field. A second spelling of `"MM initial"` there would be
 * a label that silently stops matching this one.
 */

/**
 * Tolerant of a payload that does not carry the expected arrays.
 *
 * These field names changed shape once already; a client that dereferences
 * `.length` on whatever arrived throws a TypeError and takes down the whole
 * grid, when the caller has a perfectly good "no profile available"
 * placeholder one branch away. A skewed or stale payload should degrade to
 * that placeholder, not to a blank page.
 */
export function numbers(value: unknown): number[] {
  return Array.isArray(value) ? (value as number[]) : [];
}

/**
 * The series names must not be hard-coded. `mm_profile_initial` is GAFF2 on
 * today's AFFDO path, but that is a value the artifact *declares*
 * (`TorsionScanMMProfile.force_field`), not something a chart may assert: a
 * reader will quote a legend as evidence about GAFF2 specifically, and a base
 * force field that ever varies would make the label a silent lie. Falls back
 * to the vague-but-true "MM initial" when the artifact is silent.
 */
export const MM_SERIES_FALLBACK = "MM initial";

export function mmSeriesName(profile: DihedralProfile): string {
  const declared = profile.mm_initial_force_field;
  return typeof declared === "string" && declared.length > 0 ? declared : MM_SERIES_FALLBACK;
}

/**
 * The reference curve's name — the same rule as {@link mmSeriesName}, one
 * curve over (#1365).
 *
 * The design canvas names the curves by what PRODUCED them (`GAFF2 fit`,
 * `AFFDO scan · xtb`), which is what a chemist reads a torsion panel with; the
 * baseline already did that and the reference did not, because its label was
 * the literal `"QM reference"`.
 *
 * **The canvas's own `xtb` must not be copied into the code.** In this project
 * *method* means the REFERENCE LEVEL, never the mandatory XTB pre-scan:
 * FlowStack defaults to `xtb` while the 25.11 archive runs `dft`, so a
 * hardcoded `xtb` mislabels every DFT run's reference as a semi-empirical one
 * — a claim about the science, made by a stylesheet. So the level comes from
 * the artifact's own `TorsionScanResult.qm_method` (`"B3LYP/6-31G*"` and the
 * like), projected onto `DihedralProfile.qm_method`.
 *
 * `qm_method` is optional in the record and `None` for ML producers, and an
 * older projection does not carry it at all. In every one of those cases the
 * label falls back to the bare `"QM reference"` — true of any reference level,
 * and the only honest thing to say about a level nobody recorded. Asserting a
 * default here would be the same defect as the hardcoded `xtb` with an extra
 * step.
 */
export const QM_SERIES_FALLBACK = "QM reference";

export function qmSeriesName(profile: DihedralProfile): string {
  const declared = profile.qm_method;
  return typeof declared === "string" && declared.length > 0
    ? `${QM_SERIES_FALLBACK} · ${declared}`
    : QM_SERIES_FALLBACK;
}

/**
 * Why the fitted curve is missing, when it is.
 *
 * `null` means it is not missing. `"decided"` means a positive, recorded
 * scientific decision (`fit_status` says GAFF2 was kept), which the caller
 * captions as such. `"gap"` means we do not know: the artifact predates #869,
 * the driver returned nothing, the block was dropped, or the overlay never
 * joined. `"misaligned"` means a curve arrived on a different grid.
 *
 * The `"gap"` case is the one Rupert blocked #878 over. It renders as a
 * neutral "not published" note, never as "GAFF2 retained" — a torsion whose
 * refitted parameters ARE in the force field must not be captioned as having
 * kept standard GAFF2 just because its curve did not reach the client.
 */
export type FitAbsence = "decided" | "gap" | "misaligned" | null;

export function fitAbsence(profile: DihedralProfile): FitAbsence {
  const fitted = numbers(profile.fitted_relative_kcal_per_mol);
  if (fitted.length > 0) {
    return fitted.length === numbers(profile.angles_deg).length ? null : "misaligned";
  }
  return isGaffRetained(profile) ? "decided" : "gap";
}
